/**
 * Portal Notification API Client
 *
 * Handles operations for notification center with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to notifications endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/notifications`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Notification API ====================

/**
 * Get admin notifications: pending feedback + missing group feedback.
 * Admin roles only.
 *
 * @returns {Object} { pendingFeedback: [], missingFeedback: [] }
 */
export const getAdminNotifications = async () => {
  const result = await apiRequest('getAdminNotifications', {});
  return {
    pendingFeedback: result.pendingFeedback || [],
    missingFeedback: result.missingFeedback || []
  };
};

/**
 * Get teacher notifications: missing group feedback for their course groups.
 * Teacher role only.
 *
 * @returns {Object} { missingFeedback: [] }
 */
export const getTeacherNotifications = async () => {
  const result = await apiRequest('getTeacherNotifications', {});
  return {
    missingFeedback: result.missingFeedback || []
  };
};

/**
 * Get parent notifications: aggregated approved feedback for their children.
 * Parent role only.
 *
 * @returns {Object} { children: [] }
 */
export const getParentNotifications = async () => {
  const result = await apiRequest('getParentNotifications', {});
  return {
    children: result.children || []
  };
};

const notificationPortalApi = {
  getAdminNotifications,
  getTeacherNotifications,
  getParentNotifications
};

export default notificationPortalApi;
